<template>
  <span ref="content" class="latex-renderer" v-html="renderedContent"></span>
</template>

<script>
export default {
  name: 'LatexRenderer',
  props: {
    content: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      renderedContent: '',
      mathJaxReady: false,
      renderTimer: null
    };
  },
  mounted() {
    this.loadMathJax();
  },
  watch: {
    content: {
      immediate: false,
      handler() {
        this.renderLatex();
      }
    }
  },
  beforeDestroy() {
    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
    }
  },
  methods: {
    loadMathJax() {
      // 檢查 MathJax 是否已載入
      if (window.MathJax && window.MathJax.typesetPromise) {
        this.mathJaxReady = true;
        this.renderLatex();
        return;
      }

      // 如果已有配置但還在載入中
      if (window.MathJax) {
        this.waitForMathJax();
        return;
      }

      // 配置 MathJax
      window.MathJax = {
        tex: {
          inlineMath: [['$', '$'], ['\\(', '\\)']],
          displayMath: [['$$', '$$'], ['\\[', '\\]']],
          processEscapes: true,
          processEnvironments: true
        },
        chtml: {
          fontCache: 'global',
          scale: 1.0
        },
        startup: {
          ready: () => {
            window.MathJax.startup.defaultReady();
            window.MathJaxLoaded = true;
          }
        }
      };

      // 載入 MathJax CDN（使用 CHTML 而非 SVG，兼容 html2canvas）
      if (!document.querySelector('script[src*="mathjax"]')) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js';
        script.async = true;
        script.onload = () => {
          this.waitForMathJax();
        };
        document.head.appendChild(script);
      } else {
        this.waitForMathJax();
      }
    },
    waitForMathJax() {
      // 輪詢等待 MathJax 完全載入
      const checkInterval = setInterval(() => {
        if (window.MathJax && window.MathJax.typesetPromise) {
          clearInterval(checkInterval);
          this.mathJaxReady = true;
          this.renderLatex();
        }
      }, 100);

      // 10秒後超時
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.mathJaxReady) {
          console.warn('MathJax 載入超時');
          this.renderedContent = this.escapeHtml(this.content);
        }
      }, 10000);
    },
    async renderLatex() {
      // 清除之前的定時器
      if (this.renderTimer) {
        clearTimeout(this.renderTimer);
      }

      if (!this.content) {
        this.renderedContent = '';
        return;
      }

      // 先顯示原始內容
      this.renderedContent = this.escapeHtml(this.content);

      // 等待 MathJax 載入
      if (!this.mathJaxReady) {
        return;
      }

      // 延遲渲染，避免頻繁觸發
      this.renderTimer = setTimeout(async () => {
        // 等待 DOM 更新
        await this.$nextTick();

        if (!this.$refs.content) {
          return;
        }

        try {
          // 清除之前的 MathJax 渲染
          if (window.MathJax && window.MathJax.typesetClear) {
            window.MathJax.typesetClear([this.$refs.content]);
          }

          // 使用 MathJax 重新渲染
          if (window.MathJax && window.MathJax.typesetPromise) {
            await window.MathJax.typesetPromise([this.$refs.content]);
          }
        } catch (error) {
          console.error('MathJax 渲染失敗:', error);
        }
      }, 50);
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }
};
</script>

<style scoped>
.latex-renderer {
  display: inline-block;
  line-height: 1.5;
}

.latex-renderer >>> mjx-container {
  display: inline-block !important;
  margin: 0 2px;
}
</style>
